import React, { useState } from 'react';
import { X, Filter, Copy, Search } from 'lucide-react';
import { Location } from '@/firebase/locationService';

interface AssignTemplatesPanelProps {
  location: Location;
  templates: any[];
  locations: Location[];
  onAssign: (templateIds: string[]) => Promise<void>;
  onClose: () => void;
}

const AssignTemplatesPanel: React.FC<AssignTemplatesPanelProps> = ({ 
  location, 
  templates,
  locations, 
  onAssign, 
  onClose 
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All Categories');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [assignedTemplateIds, setAssignedTemplateIds] = useState<string[]>(location.assignedTemplates || []);

  // Get unique categories and risk levels from templates
  const categories = Array.from(new Set(templates.map(t => t.category)));
  const riskLevels = Array.from(new Set(templates.map(t => t.risk_level)));

  // Filter templates by category, risk level, and search term
  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'All Categories' || template.category === selectedCategory;
    const matchesRisk = selectedRiskLevel === 'All' || template.risk_level === selectedRiskLevel;
    const matchesSearch = !searchTerm || 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.owner_role?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesRisk && matchesSearch;
  });

  const toggleTemplate = (templateId: string) => {
    setAssignedTemplateIds(prev => {
      const isSelected = prev.includes(templateId);
      if (isSelected) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allFilteredIds = filteredTemplates.map(t => t.id).filter(Boolean) as string[];
      setAssignedTemplateIds(prev => [...new Set([...prev, ...allFilteredIds])]);
    } else {
      const allFilteredIds = filteredTemplates.map(t => t.id).filter(Boolean) as string[];
      setAssignedTemplateIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
    }
  };

  const areAllFilteredSelected = filteredTemplates.length > 0 && 
    filteredTemplates.every(t => assignedTemplateIds.includes(t.id));

  // Get other locations (excluding current)
  const otherLocations = locations.filter(l => l.id !== location.id);

  const handleCopyFromLocation = (sourceLocationId: string) => {
    const sourceLocation = locations.find(l => l.id === sourceLocationId);
    if (sourceLocation && sourceLocation.assignedTemplates) {
      setAssignedTemplateIds(sourceLocation.assignedTemplates);
    }
  };

  const handleSave = async () => {
    await onAssign(assignedTemplateIds);
    onClose();
  };

  return (
  <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-end z-[120]">
      <div className="bg-white w-full max-w-2xl h-full flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Assign Compliances</h2>
              <p className="text-sm text-gray-600 mt-1">{location.name}</p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Copy from Location */}
          {otherLocations.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <Copy className="w-4 h-4 text-gray-600" />
                <label className="text-sm font-medium text-gray-700">Copy from Location:</label>
                <select
                  onChange={(e) => e.target.value && handleCopyFromLocation(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  defaultValue=""
                >
                  <option value="">Select a location to copy...</option>
                  {otherLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} ({loc.assignedTemplates?.length || 0} compliances)
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search compliances by name, category, or owner role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-md">
              <Filter className="w-4 h-4 text-gray-600" />
            </div>
            
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="All Categories">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Risk Level Filter */}
            <select
              value={selectedRiskLevel}
              onChange={(e) => setSelectedRiskLevel(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="All">All Risk Levels</option>
              {riskLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Compliance List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No compliances found matching your filters.</p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={areAllFilteredSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Select All ({filteredTemplates.length} compliances)
                  </span>
                </label>
              </div>

              {/* Compliances List */}
              <div className="space-y-2">
                {filteredTemplates.map(template => (
                  <label
                    key={template.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                    title={`${template.name}

Category: ${template.category}
Frequency: ${template.frequency}
Risk Level: ${template.risk_level}
Owner Role: ${template.owner_role}

Criteria: ${template.pass_fail_criteria}

Evidence Required:
${template.evidence_types.map((e: any) => `• ${e.label} (${e.type})${e.required ? ' - REQUIRED' : ''}`).join('\n')}`}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {template.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {template.category} • Risk: {template.risk_level}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={assignedTemplateIds.includes(template.id)}
                      onChange={() => template.id && toggleTemplate(template.id)}
                      className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {assignedTemplateIds.length} compliance{assignedTemplateIds.length !== 1 ? 's' : ''} assigned
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Save Assignment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignTemplatesPanel;

